import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, Store, Eye, BarChart3, CalendarDays, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fetchMyProfiles, fetchMyInquiries, deleteProfile, fetchMyListings, fetchMyEvents, deleteHostEvent } from "@/lib/api";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [listings, setListings] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([fetchMyProfiles(), fetchMyInquiries().catch(() => []), fetchMyListings().catch(() => []), fetchMyEvents().catch(() => [])])
      .then(([p, i, l, ev]) => {
        setProfiles(p);
        setInquiries(i);
        setListings(l);
        setEvents(ev);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this profile permanently?")) return;
    try {
      await deleteProfile(id);
      setProfiles((prev) => prev.filter((p) => p.id !== id));
      toast.success("Profile deleted");
    } catch {
      toast.error("Could not delete profile");
    }
  };

  const onDeleteEvent = async (id) => {
    if (!window.confirm("Delete this event permanently?")) return;
    try {
      await deleteHostEvent(id);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      toast.success("Event deleted");
    } catch {
      toast.error("Could not delete event");
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div data-testid="dashboard-page" className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-3">Member Dashboard</div>
          <h1 className="font-serif font-black text-4xl md:text-6xl leading-[1.02] text-sand-900">
            {user?.name || "Welcome"}.
          </h1>
          <p className="mt-3 text-sand-700">{user?.email}</p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/profile/new"
            data-testid="dashboard-new-profile"
            className="bg-sand-900 text-sand-50 px-5 py-3 font-mono text-[11px] font-bold tracking-[0.2em] uppercase hover:bg-brand-500 transition-colors flex items-center gap-2"
          >
            <Plus size={14} /> New Profile
          </Link>
          <Link
            to="/events/new"
            data-testid="dashboard-new-event"
            className="bg-brand-500 text-sand-50 px-5 py-3 font-mono text-[11px] font-bold tracking-[0.2em] uppercase hover:bg-brand-600 transition-colors flex items-center gap-2"
          >
            <CalendarDays size={14} /> Host Event
          </Link>
          <button
            onClick={doLogout}
            data-testid="dashboard-logout"
            className="border border-sand-300 px-5 py-3 font-mono text-[11px] font-bold tracking-[0.2em] uppercase text-sand-900 hover:border-brand-500 transition-colors flex items-center gap-2"
          >
            <LogOut size={14} /> Log Out
          </button>
        </div>
      </div>

      {/* Analytics */}
      <section className="mb-16" data-testid="analytics-section">
        <h2 className="font-serif text-2xl md:text-3xl font-bold text-sand-900 border-b-2 border-sand-900 pb-3 mb-8 flex items-center gap-2">
          <BarChart3 size={20} /> Analytics
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {(() => {
            const profileViews = profiles.reduce((s, p) => s + (p.views || 0), 0);
            const listingViews = listings.reduce((s, l) => s + (l.views || 0), 0);
            const stats = [
              { label: "Total Views", value: profileViews + listingViews, testid: "stat-total-views" },
              { label: "Profile Views", value: profileViews, testid: "stat-profile-views" },
              { label: "Listing Views", value: listingViews, testid: "stat-listing-views" },
              { label: "Verified", value: [...profiles, ...listings].filter((x) => x.verified).length, testid: "stat-verified" },
            ];
            return stats.map((s) => (
              <div key={s.label} className="border border-sand-200 bg-white p-5" data-testid={s.testid}>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-sand-600 mb-2 flex items-center gap-1.5">
                  <Eye size={12} /> {s.label}
                </div>
                <div className="font-serif text-4xl font-black text-sand-900">{s.value}</div>
              </div>
            ));
          })()}
        </div>
      </section>

      {/* My Profiles */}
      <section className="mb-20">
        <h2 className="font-serif text-2xl md:text-3xl font-bold text-sand-900 border-b-2 border-sand-900 pb-3 mb-8">
          My Profiles
        </h2>
        {loading ? (
          <div className="font-mono text-sm text-sand-600">Loading…</div>
        ) : profiles.length === 0 ? (
          <div data-testid="no-profiles" className="py-10 text-center border border-dashed border-sand-300">
            <p className="font-serif italic text-xl text-sand-700 mb-4">You haven't created a profile yet.</p>
            <Link to="/profile/new" className="font-mono text-[11px] tracking-[0.25em] uppercase text-brand-500 editorial-link">
              Create your first profile →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="my-profiles-list">
            {profiles.map((p) => (
              <div key={p.id} className="border border-sand-200 bg-white p-6 flex items-start justify-between gap-4">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-forest-500 mb-1">{p.type} · {p.city}</div>
                  <Link to={`/profile/${p.id}`} className="font-serif text-2xl font-bold text-sand-900 hover:text-brand-500 transition-colors">
                    {p.name}
                  </Link>
                  {p.bio && <p className="mt-2 text-sm text-sand-700 line-clamp-2 font-light">{p.bio}</p>}
                  <div className="mt-2 font-mono text-[10px] tracking-widest uppercase text-sand-600 flex items-center gap-1.5">
                    <Eye size={11} /> {p.views || 0} views{p.verified ? " · Verified" : ""}
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Link
                    to={`/profile/${p.id}/edit`}
                    data-testid={`edit-profile-${p.id}`}
                    className="p-2 border border-sand-300 hover:border-brand-500 text-sand-900"
                    aria-label="Edit"
                  >
                    <Pencil size={14} />
                  </Link>
                  <button
                    onClick={() => onDelete(p.id)}
                    data-testid={`delete-profile-${p.id}`}
                    className="p-2 border border-sand-300 hover:border-brand-500 hover:text-brand-500 text-sand-900"
                    aria-label="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* My Events */}
      <section className="mb-20" data-testid="my-events-section">
        <h2 className="font-serif text-2xl md:text-3xl font-bold text-sand-900 border-b-2 border-sand-900 pb-3 mb-8 flex items-center gap-2">
          <CalendarDays size={20} /> My Events
        </h2>
        {events.length === 0 ? (
          <div data-testid="no-events" className="py-8 text-sand-700 font-light">
            You haven't hosted any events yet.{" "}
            <Link to="/events/new" className="text-brand-500 editorial-link">Host your first event →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="my-events-list">
            {events.map((ev) => (
              <div key={ev.id} className="border border-sand-200 bg-white p-6 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-forest-500 mb-1 flex items-center gap-1.5">
                    <CalendarDays size={12} /> {ev.category} · {ev.city}
                  </div>
                  <Link to={`/events/${ev.id}`} className="font-serif text-2xl font-bold text-sand-900 hover:text-brand-500 transition-colors">
                    {ev.name}
                  </Link>
                  <div className="mt-2 flex flex-wrap items-center gap-3 font-mono text-[10px] tracking-widest uppercase">
                    <span className={`px-2 py-1 ${
                      ev.status === "published" ? "bg-forest-500 text-sand-50" :
                      ev.status === "pending" ? "bg-ochre-400 text-sand-900" : "bg-brand-500 text-sand-50"
                    }`} data-testid={`event-status-${ev.id}`}>{ev.status}</span>
                    <span className="text-sand-600 flex items-center gap-1.5"><Users size={11} /> {ev.rsvp_count || 0} going</span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Link
                    to={`/events/${ev.id}/edit`}
                    data-testid={`edit-event-${ev.id}`}
                    className="p-2 border border-sand-300 hover:border-brand-500 text-sand-900"
                    aria-label="Edit event"
                  >
                    <Pencil size={14} />
                  </Link>
                  <button
                    onClick={() => onDeleteEvent(ev.id)}
                    data-testid={`delete-event-${ev.id}`}
                    className="p-2 border border-sand-300 hover:border-brand-500 hover:text-brand-500 text-sand-900"
                    aria-label="Delete event"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* My Claimed Listings */}
      <section className="mb-20">
        <h2 className="font-serif text-2xl md:text-3xl font-bold text-sand-900 border-b-2 border-sand-900 pb-3 mb-8">
          My Claimed Listings
        </h2>        {listings.length === 0 ? (
          <div data-testid="no-listings" className="py-8 text-sand-700 font-light">
            You haven't claimed any directory listings yet. Find your business in the{" "}
            <Link to="/directory" className="text-brand-500 editorial-link">Directory</Link> and claim it.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="my-listings-list">
            {listings.map((l) => (
              <div key={l.id} className="border border-sand-200 bg-white p-6 flex items-start justify-between gap-4">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-forest-500 mb-1 flex items-center gap-1.5">
                    <Store size={12} /> {l.category} · {l.city}
                  </div>
                  <Link to={`/directory/${l.id}`} className="font-serif text-2xl font-bold text-sand-900 hover:text-brand-500 transition-colors">
                    {l.name}
                  </Link>
                  <p className="mt-2 text-sm text-sand-700 line-clamp-2 font-light">{l.short_description}</p>
                  <div className="mt-2 font-mono text-[10px] tracking-widest uppercase text-sand-600 flex items-center gap-1.5">
                    <Eye size={11} /> {l.views || 0} views{l.verified ? " · Verified" : ""}
                  </div>
                </div>
                <Link
                  to={`/directory/${l.id}/edit`}
                  data-testid={`edit-listing-${l.id}`}
                  className="p-2 border border-sand-300 hover:border-brand-500 text-sand-900 shrink-0"
                  aria-label="Edit listing"
                >
                  <Pencil size={14} />
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* My Inquiries */}
      <section>
        <h2 className="font-serif text-2xl md:text-3xl font-bold text-sand-900 border-b-2 border-sand-900 pb-3 mb-8">
          My Business Inquiries
        </h2>
        {inquiries.length === 0 ? (
          <div data-testid="no-inquiries" className="py-8 text-sand-700 font-light">
            No inquiries submitted under {user?.email} yet.{" "}
            <Link to="/services" className="text-brand-500 editorial-link">Pitch your brand →</Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="my-inquiries-list">
            {inquiries.map((q) => (
              <div key={q.id} className="border border-sand-200 bg-white p-5">
                <div className="flex items-center justify-between gap-4">
                  <span className="font-serif text-xl font-bold text-sand-900">{q.business_name}</span>
                  <span className="font-mono text-[9px] tracking-widest uppercase bg-sand-100 px-2 py-1">{q.status}</span>
                </div>
                {q.services?.length > 0 && (
                  <div className="mt-2 font-mono text-[10px] tracking-widest uppercase text-forest-500">
                    {q.services.join(" · ")}
                  </div>
                )}
                <p className="mt-2 text-sm text-sand-700 font-light line-clamp-2">{q.message}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
